﻿import React, { useState, useEffect } from 'react';
import { Replicate } from '../../models/AnalyticalPlans/replicate';
import { LookupItem } from '../../models/lookup-item';
import { ReplicatesRequest } from '../../models/AnalyticalPlans/replicates-request';
import ReplicatesVisualizerComponent from './ReplicatesVisualizerComponent';
import { ReplicateDefaultSample } from '../../models/AnalyticalPlans/replicate-default-sample';
import { AnalyticalPlanListItem } from '../../models/AnalyticalPlans/analytical-plan-list-item';
import { CrudFunctions } from '../../utilities/crud-functions';
import GlobalDialog from '../Modal/GlobalDialog';
import { ModalMetadata } from '../../models/modal-metadata';
import { ModalMessageType } from '../../models/enums';
import DmaFooter from '../DmaFooter';

import Button from 'devextreme-react/button';
import Toast from 'devextreme-react/toast';
import List from 'devextreme-react/list';
import NumberBox from 'devextreme-react/number-box';
import TextBox from 'devextreme-react/text-box';

export interface Props {
    analyticalPlan: AnalyticalPlanListItem;
    standardId: string;
    isReadOnly: boolean;
    onClose?: () => void;
}

export default function ReplicatesComponent(props: Props) {
    const [sampleTypes, setSampleTypes] =
        useState<LookupItem[]>();

    const [sampleReplicate, setSampleReplicate] =
        useState<Replicate>();

    const [standardReplicate, setStandardReplicate] =
        useState<Replicate>();

    const [defaultSamples, setDefaultSamples] =
        useState<ReplicateDefaultSample[]>();

    const [defaultStandardSamples, setDefaultStandardSamples] =
        useState<ReplicateDefaultSample[]>();

    const [selectedDefaultSample, setSelectedDefaultSample] =
        useState<ReplicateDefaultSample>();

    const [selectedDefaultStandardSample, setSelectedDefaultStandardSample] =
        useState<ReplicateDefaultSample>();

    const [originalSamplesString, setOriginalSamplesString] =
        useState<string>();

    const [originalStandardsString, setOriginalStandardsString] =
        useState<string>();

    const [toastVisible, setToastVisible] =
        useState<boolean>();

    const [toastMessage, setToastMessage] =
        useState<string>();

    const [toastType, setToastType] =
        useState<any>();

    const [globalDialogMetadata, setGlobalDialogMetadata] =
        useState<ModalMetadata>();

    const [globalDialogVisible, setGlobalDialogVisible] =
        useState<boolean>();

    useEffect(() => {
        getAllData();
    }, [props.analyticalPlan]);

    const getTitle = () => {
        if (props.analyticalPlan && props.analyticalPlan.standards && props.standardId) {

            let matchingStandard = props.analyticalPlan.standards.find(s => s.standardId === props.standardId);

            let title = "";

            if (matchingStandard.manufacturerName)
                title += matchingStandard.manufacturerName;

            if (matchingStandard.productName)
                title += ` - ${matchingStandard.productName}`;

            if (matchingStandard.catalogNumber)
                title += ` (${matchingStandard.catalogNumber})`;

            return title;
        }

        return "";
    };

    return (
        <div>
            <div className="pw-comp-row pw-margin-top-05">
                <div className="save-toolbar pw-margin-right-05">
                    <Button id="saveButton" text="Save" icon="save" type="success"
                        className="pw-button"
                        hint="save changes"
                        height={'1.8rem'}
                        onClick={onSaveClick} />

                    <Button id="cancelButton" text="Close" icon="close" type="normal"
                        className="pw-button"
                        hint="close dialog"
                        height={'1.8rem'}
                        onClick={onCloseClick} />
                </div>

                <div className="lq-sub-title pw-margin-left-1 pw-margin-top-05">{getTitle()}</div>
            </div>

            <div className="pw-comp-row pw-margin-top-1">
                <div className="pw-default-light-bg">

                    <div className="pw-comp-row">
                        <div className="pw-form-unit pw-comp-20">
                            <div className="pw-form-unit-label">Samples</div>
                            <List items={defaultSamples ? defaultSamples : []}
                                keyExpr="replicateDefaultId"
                                className="dx-listbox"
                                itemRender={sampleReplicatesRender}
                                height="8rem" selectionMode="single"
                                showSelectionControls={true}
                                onSelectionChanged={onSelectedSampleChanged}
                                selectedItem={selectedDefaultSample ? selectedDefaultSample : undefined} />
                        </div>

                        {(defaultSamples && defaultSamples.length > 0) &&
                            <Button id="applyDefaultSampleReplicatesButton"
                                text="Apply" type="success"
                                className="pw-row-button-11 pw-margin-right-1"
                                hint="close dialog" disabled={!selectedDefaultSample}
                                height='1.8rem'
                                onClick={onApplyDefaultSampleReplicatesClick} />
                        }
                    </div>

                    <div className="pw-comp-row">
                        <div className="pw-form-unit">
                            <div className="pw-form-unit-label">Preparations</div>
                            <NumberBox id="preparations" className="textbox" width="7rem"
                                onValueChanged={sampleReplicateValueChanged}
                                value={sampleReplicate && sampleReplicate.preparations ?
                                    sampleReplicate.preparations : undefined} />
                        </div>

                        <div className="pw-margin-left-1">
                            <div className="pw-form-unit-label">Injections</div>
                            <NumberBox id="injections" className="textbox" width="7rem"
                                onValueChanged={sampleReplicateValueChanged}
                                value={sampleReplicate && sampleReplicate.injections ?
                                    sampleReplicate.injections : undefined} />
                        </div>
                    </div>

                    <div className="pw-comp-row">
                        <div className="pw-form-unit pw-comp-20">
                            <div className="pw-form-unit-label">Sample Types</div>
                            <TextBox id="samplesString" className="textbox"
                                onValueChanged={sampleReplicateValueChanged}
                                value={sampleReplicate && sampleReplicate.samplesString ?
                                    sampleReplicate.samplesString : undefined} />
                        </div>
                    </div>

                    <ReplicatesVisualizerComponent containerId="1" sampleTypes={sampleTypes}
                        replicate={sampleReplicate ? sampleReplicate : undefined} />

                </div>

                <div className="pw-margin-left-1 pw-default-light-bg">
                    <div className="pw-comp-row">

                        <div className="pw-form-unit pw-comp-20">
                            <div className="pw-form-unit-label">Standards</div>
                            <List items={defaultStandardSamples ? defaultStandardSamples : []}
                                keyExpr="replicateDefaultId"
                                className="dx-listbox"
                                itemRender={sampleReplicatesRender}
                                height="8rem" selectionMode="single"
                                showSelectionControls={true}
                                onSelectionChanged={onSelectedStandardSampleChanged}
                                selectedItem={selectedDefaultStandardSample ? selectedDefaultStandardSample : undefined} />
                        </div>

                        {(defaultStandardSamples && defaultStandardSamples.length > 0) &&
                            <Button id="applyDefaultSampleReplicatesButton"
                                text="Apply" type="success" disabled={!selectedDefaultStandardSample}
                                className="pw-row-button-11 pw-margin-right-1"
                                hint="close dialog"
                                height='1.8rem'
                                onClick={onApplyDefaultStandardReplicatesClick} />
                        }
                    </div>

                    <div className="pw-comp-row">
                        <div className="pw-form-unit">
                            <div className="pw-form-unit-label">Preparations</div>
                            <NumberBox id="preparations" className="textbox" width="7rem"
                                onValueChanged={standardReplicateValueChanged}
                                value={standardReplicate && standardReplicate.preparations ?
                                    standardReplicate.preparations : undefined} />
                        </div>

                        <div className="pw-margin-left-1">
                            <div className="pw-form-unit-label">Injections</div>
                            <NumberBox id="injections" className="textbox" width="7rem"
                                onValueChanged={standardReplicateValueChanged}
                                value={standardReplicate && standardReplicate.injections ?
                                    standardReplicate.injections : undefined} />
                        </div>
                    </div>

                    <div className="pw-comp-row">
                        <div className="pw-form-unit pw-comp-20">
                            <div className="pw-form-unit-label">Sample Types</div>
                            <TextBox id="samplesString" className="textbox"
                                onValueChanged={standardReplicateValueChanged}
                                value={standardReplicate && standardReplicate.samplesString ?
                                    standardReplicate.samplesString : undefined} />
                        </div>

                    </div>

                    <ReplicatesVisualizerComponent containerId="2" sampleTypes={sampleTypes}
                        replicate={standardReplicate ? standardReplicate : undefined} />

                </div>
            </div>

            <DmaFooter />

            <Toast visible={toastVisible}
                message={toastMessage}
                type={toastType}
                onHiding={onToastHiding}
                displayTime={600} />

            {globalDialogVisible &&
                <GlobalDialog metadata={globalDialogMetadata} />
            }

        </div>
    );

    function onApplyDefaultSampleReplicatesClick() {
        if (selectedDefaultSample) {
            let replicate: Replicate = {
                preparations: selectedDefaultSample.preparations,
                injections: selectedDefaultSample.injections,
                samplesString: selectedDefaultSample.sampleNames
            };

            setSampleReplicate(replicate);
        }
    }

    function onApplyDefaultStandardReplicatesClick() {
        if (selectedDefaultStandardSample) {

            let replicate: Replicate = {
                preparations: selectedDefaultStandardSample.preparations,
                injections: selectedDefaultStandardSample.injections,
                samplesString: selectedDefaultStandardSample.sampleNames,
                isStandard: true
            };

            setStandardReplicate(replicate);
        }
    }

    function sampleReplicateValueChanged(e: any) {
        let caller = e.element.id;

        setSampleReplicate({ ...sampleReplicate, [caller]: e.value })
    }

    function standardReplicateValueChanged(e: any) {
        let caller = e.element.id;

        setStandardReplicate({ ...standardReplicate, [caller]: e.value })
    }

    async function onSaveClick() {
        if (props.analyticalPlan && props.analyticalPlan.analyticalPlanId && props.standardId) {
            let request: ReplicatesRequest = {
                analyticalPlanId: props.analyticalPlan.analyticalPlanId,
                standardId: props.standardId,
                replicates: []
            };

            if (sampleReplicate && sampleReplicate.preparations) {
                request.replicates.push(sampleReplicate);
                request.sampleSamplesChanged = sampleReplicate.samplesString !== originalSamplesString;
            }

            if (standardReplicate && standardReplicate.preparations) {
                request.replicates.push(standardReplicate);
                request.standardSamplesChanged = standardReplicate.samplesString !== originalStandardsString;
            }

            await updateReplicates(request)
                .then(data => {
                    showToast("Replicates Updated Successfully", "success");
                })
                .catch((res) => {
                    showMessage(res, ModalMessageType.Error, "Error Updating Replicates");
                });
        }
    }

    function onCloseClick() {
        if (props.onClose)
            props.onClose();
    }

    function onSelectedSampleChanged(e: any) {
        if (e.addedItems && e.addedItems.length > 0) {
            const sample: ReplicateDefaultSample = e.addedItems[0];

            setSelectedDefaultSample(sample);
        }
    }

    function onSelectedStandardSampleChanged(e: any) {
        if (e.addedItems && e.addedItems.length > 0) {
            const sample: ReplicateDefaultSample = e.addedItems[0];

            setSelectedDefaultStandardSample(sample);
        }
    }

    async function getDefaultSampleReplicates() {
        if (props.analyticalPlan) {
            await getDefaultReplicates()
                .then(data => {
                    let defaultSamples = data as ReplicateDefaultSample[];

                    if (defaultSamples) {
                        setDefaultSamples(defaultSamples.filter(s => !s.isStandard));
                        setDefaultStandardSamples(defaultSamples.filter(s => s.isStandard));
                    }
                })
                .catch((res) => {
                    showMessage(res, ModalMessageType.Error, "Error Retrieving Default Replicates");
                });
        }
    }

    function onToastHiding() {
        setToastVisible(false);
        setToastMessage(undefined);
        setToastType(undefined);
    }

    function showToast(message: string, toastType: string) {
        setToastMessage(message);
        setToastType(toastType);
        setToastVisible(true);
    }

    // get functions

    async function getSampleTypes() {
        await CrudFunctions.getData("common/sampletypes")
            .then((data) => {
                setSampleTypes(data);
            })
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Retrieving Sample Types");
            });
    }

    async function getDefaultReplicates() {
        if (props.analyticalPlan && props.analyticalPlan.analyticalPlanId)
            return await CrudFunctions.getData(`analyticalplans/replicatedefaultsamples/${props.analyticalPlan.analyticalPlanId}`);
    }

    async function getAnalyticalPlanReplicates() {
        if (props.analyticalPlan && props.analyticalPlan.analyticalPlanId && props.standardId) {
            await CrudFunctions.getData(`analyticalplans/replicates/${props.analyticalPlan.analyticalPlanId}/${props.standardId}`)
                .then(data => {
                    let replicates = data as Replicate[];

                    //console.log("replicates");
                    //console.log(data);

                    if (replicates) {
                        let sampleRep = replicates.find(r => !r.isStandard);
                        let stdRep = replicates.find(r => r.isStandard);

                        if (sampleRep) {
                            setSampleReplicate(sampleRep);
                            setOriginalSamplesString(sampleRep.samplesString);
                        }
                        else
                            setOriginalSamplesString(null);

                        if (stdRep) {
                            stdRep.isStandard = true;
                            setStandardReplicate(stdRep);
                            setOriginalStandardsString(stdRep.samplesString);
                        }
                        else
                            setOriginalStandardsString(null);
                    }
                })
                .catch((res) => {
                    showMessage(res, ModalMessageType.Error, "Error Retrieving Replicates");
                });
        }
    }

    async function updateReplicates(request: ReplicatesRequest) {
        return await CrudFunctions.putData("analyticalplans/replicates", request);
    }

    async function getAllData() {
        await Promise.all([
            getSampleTypes(),
            getDefaultSampleReplicates(),
            getAnalyticalPlanReplicates()
        ]);
    }

    //render

    function sampleReplicatesRender(sampleReplicate: ReplicateDefaultSample) {
        let scopeName = sampleReplicate.scopeType ? ` - (${sampleReplicate.scopeName})` : '';

        return (
            <div>
                <div>{sampleReplicate.replicateSampleString}{scopeName}</div>
            </div>
        );
    }

    //other

    function showMessage(res: any, mode: ModalMessageType, title: string) {
        let mm: ModalMetadata = {
            error: res,
            mode: mode,
            title: title
        };

        setGlobalDialogMetadata(mm);
        setGlobalDialogVisible(true);
    }
}



